import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Briefcase,
  Database,
  FileText,
  List,
  Plus,
  Search,
  Ticket,
} from 'lucide-react';
import DefineCreditLoanForm from './DefineCreditLoanForm';
import {
  blotterLoanPath,
  iborPositionPath,
  leveragedLoanTicketPath,
  leveragedLoanTicketState,
  loan360Path,
  privateCreditTicketPath,
  securityMasterPath,
  type LoanSource,
} from '../../utils/loanNavigation';

interface CreditLoanReferenceWorkspaceProps {
  initialAssetClass?: string;
  openNewOnMount?: boolean;
  initialQuery?: string;
}

interface ReferenceLoan {
  id: number | string;
  source: LoanSource;
  loanName: string;
  borrowerName: string;
  facilityType?: string;
  status?: string;
  loanId?: string;
  cusip?: string;
  securityId?: number;
  portfolioName?: string;
  agentBank?: string;
  maturityDate?: string;
}

const asArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  return [];
};

const normalizeLoan = (loan: any, source: LoanSource): ReferenceLoan => ({
  id: loan.id,
  source,
  loanName: loan.loanName || loan.security?.securityName || loan.borrowerName || `Loan ${loan.id}`,
  borrowerName: loan.borrowerName || loan.borrower?.borrowerName || '--',
  facilityType: loan.facilityType,
  status: loan.status,
  loanId: loan.loanId,
  cusip: loan.cusip || loan.security?.cusip,
  securityId: loan.securityId || loan.security?.id,
  portfolioName: loan.portfolioName || loan.portfolio?.portfolioName || loan.portfolio?.portfolioCode,
  agentBank: loan.agentBank,
  maturityDate: loan.maturityDate,
});

const Workspace = styled.div`
  display: grid;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  flex-wrap: wrap;
`;

const SearchBox = styled.label`
  min-width: min(420px, 100%);
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 38px;
  padding: 0 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  color: #64748b;

  &:focus-within { border-color: #38bdf8; }

  input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #f8fafc;
    font-size: 0.82rem;
  }
`;

const Select = styled.select`
  min-height: 38px;
  padding: 0 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 8px;
  background: rgba(2, 6, 23, 0.72);
  color: #e2e8f0;
`;

const Button = styled.button<{ $primary?: boolean }>`
  min-height: 38px;
  padding: 0 0.8rem;
  border: 1px solid ${({ $primary }) => $primary ? '#2563eb' : 'rgba(148, 163, 184, 0.24)'};
  border-radius: 8px;
  background: ${({ $primary }) => $primary ? '#2563eb' : 'rgba(15, 23, 42, 0.72)'};
  color: #f8fafc;
  font-size: 0.76rem;
  font-weight: 750;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover { border-color: #38bdf8; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1040px;

  th {
    padding: 0.7rem 0.8rem;
    text-align: left;
    color: #94a3b8;
    background: rgba(15, 23, 42, 0.86);
    font-size: 0.66rem;
    text-transform: uppercase;
    font-weight: 800;
  }

  td {
    padding: 0.75rem 0.8rem;
    border-top: 1px solid rgba(148, 163, 184, 0.12);
    color: #cbd5e1;
    font-size: 0.78rem;
    vertical-align: middle;
  }
`;

const LoanName = styled.div`
  color: #f8fafc;
  font-weight: 750;
`;

const Subtext = styled.div`
  margin-top: 0.15rem;
  color: #64748b;
  font-size: 0.68rem;
`;

const SourceBadge = styled.span<{ $source: LoanSource }>`
  display: inline-flex;
  padding: 0.18rem 0.42rem;
  border-radius: 5px;
  border: 1px solid ${({ $source }) => $source === 'LEVERAGED_LOAN' ? 'rgba(56,189,248,.35)' : 'rgba(167,139,250,.35)'};
  color: ${({ $source }) => $source === 'LEVERAGED_LOAN' ? '#7dd3fc' : '#c4b5fd'};
  font-size: 0.64rem;
  font-weight: 800;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.35rem;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(15, 23, 42, 0.76);
  color: #bae6fd;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: #38bdf8; color: #f8fafc; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const State = styled.div<{ $error?: boolean }>`
  padding: 1rem;
  border: 1px solid ${({ $error }) => $error ? 'rgba(248,113,113,.3)' : 'rgba(148,163,184,.16)'};
  border-radius: 8px;
  color: ${({ $error }) => $error ? '#fecaca' : '#94a3b8'};
  font-size: 0.8rem;
`;

const CreditLoanReferenceWorkspace: React.FC<CreditLoanReferenceWorkspaceProps> = ({
  initialAssetClass,
  openNewOnMount = false,
  initialQuery = '',
}) => {
  const navigate = useNavigate();
  const [defining, setDefining] = useState(openNewOnMount);
  const [loans, setLoans] = useState<ReferenceLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState(initialQuery);
  const [sourceFilter, setSourceFilter] = useState<'ALL' | LoanSource>('ALL');

  const loadLoans = useCallback(async () => {
    setLoading(true);
    setError(null);
    const results = await Promise.allSettled([
      axios.get('/api/credit/loans'),
      axios.get('/api/private-credit/loans'),
    ]);
    const leveraged = results[0].status === 'fulfilled'
      ? asArray(results[0].value.data).map(row => normalizeLoan(row, 'LEVERAGED_LOAN'))
      : [];
    const privateCredit = results[1].status === 'fulfilled'
      ? asArray(results[1].value.data).map(row => normalizeLoan(row, 'PRIVATE_CREDIT'))
      : [];
    setLoans([...leveraged, ...privateCredit].sort((a, b) => a.loanName.localeCompare(b.loanName)));
    if (results.every(result => result.status === 'rejected')) {
      setError('Loan reference inventory is unavailable. No synthetic rows were substituted.');
    } else if (results.some(result => result.status === 'rejected')) {
      setError('One loan source is unavailable; available system records are shown.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadLoans(); }, [loadLoans]);

  const visibleLoans = useMemo(() => {
    const term = query.trim().toLowerCase();
    return loans.filter(loan => {
      if (sourceFilter !== 'ALL' && loan.source !== sourceFilter) return false;
      if (!term) return true;
      return [
        loan.loanName,
        loan.borrowerName,
        loan.loanId,
        loan.cusip,
        loan.facilityType,
        loan.status,
        loan.agentBank,
        loan.portfolioName,
      ].some(value => String(value || '').toLowerCase().includes(term));
    });
  }, [loans, query, sourceFilter]);

  const openTicket = (loan: ReferenceLoan) => {
    const path = loan.source === 'LEVERAGED_LOAN'
      ? leveragedLoanTicketPath(loan.cusip)
      : privateCreditTicketPath(loan.cusip);
    navigate(path, leveragedLoanTicketState({
      securityId: loan.securityId,
      cusip: loan.cusip,
      securityName: loan.loanName,
      loanId: loan.id,
      loanName: loan.loanName,
      borrowerName: loan.borrowerName,
      facilityType: loan.facilityType,
    }));
  };

  if (defining) {
    return (
      <Workspace>
        <Toolbar>
          <Button onClick={() => { setDefining(false); loadLoans(); }}>
            <ArrowLeft size={15} /> Loan inventory
          </Button>
        </Toolbar>
        <DefineCreditLoanForm initialAssetClass={initialAssetClass} />
      </Workspace>
    );
  }

  return (
    <Workspace>
      <Toolbar>
        <Filters>
          <SearchBox>
            <Search size={15} />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search CUSIP, borrower, loan, facility, agent or portfolio"
            />
          </SearchBox>
          <Select value={sourceFilter} onChange={event => setSourceFilter(event.target.value as 'ALL' | LoanSource)}>
            <option value="ALL">All loan sources</option>
            <option value="LEVERAGED_LOAN">Leveraged Loan</option>
            <option value="PRIVATE_CREDIT">Private Credit</option>
          </Select>
        </Filters>
        <Button $primary onClick={() => setDefining(true)}><Plus size={15} /> Define Loan</Button>
      </Toolbar>

      {error && <State $error>{error}</State>}
      {loading ? (
        <State>Loading governed loan reference records...</State>
      ) : visibleLoans.length === 0 ? (
        <State>No loan reference records match the current filters.</State>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Loan / Borrower</th>
                <th>Identifier</th>
                <th>Facility</th>
                <th>Status</th>
                <th>Agent / Portfolio</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleLoans.map(loan => (
                <tr key={`${loan.source}-${loan.id}`}>
                  <td><SourceBadge $source={loan.source}>{loan.source === 'LEVERAGED_LOAN' ? 'LL' : 'PC'}</SourceBadge></td>
                  <td>
                    <LoanName>{loan.loanName}</LoanName>
                    <Subtext>{loan.borrowerName}</Subtext>
                  </td>
                  <td>
                    <LoanName>{loan.cusip || loan.loanId || '--'}</LoanName>
                    <Subtext>{loan.maturityDate ? `Matures ${loan.maturityDate}` : 'Maturity not mapped'}</Subtext>
                  </td>
                  <td>{(loan.facilityType || '--').replace(/_/g, ' ')}</td>
                  <td>{(loan.status || '--').replace(/_/g, ' ')}</td>
                  <td>
                    <LoanName>{loan.agentBank || '--'}</LoanName>
                    <Subtext>{loan.portfolioName || 'No committed portfolio on reference record'}</Subtext>
                  </td>
                  <td>
                    <RowActions>
                      <IconButton title="Open Loan 360" onClick={() => navigate(loan360Path(loan.source, loan.id))}>
                        <BookOpen size={15} />
                      </IconButton>
                      <IconButton title="Open governed edit workflow" onClick={() => navigate(loan360Path(loan.source, loan.id, 'underwriting'))}>
                        <FileText size={15} />
                      </IconButton>
                      <IconButton title="Open Security Master" disabled={!loan.cusip} onClick={() => loan.cusip && navigate(securityMasterPath(loan.cusip))}>
                        <Database size={15} />
                      </IconButton>
                      <IconButton title="Create prefilled acquisition ticket" disabled={!loan.securityId && !loan.cusip} onClick={() => openTicket(loan)}>
                        <Ticket size={15} />
                      </IconButton>
                      <IconButton title="Open trade lineage in Blotter" disabled={!loan.cusip} onClick={() => loan.cusip && navigate(blotterLoanPath(loan.cusip))}>
                        <List size={15} />
                      </IconButton>
                      <IconButton title="Open portfolio exposure in IBOR" disabled={!loan.cusip} onClick={() => loan.cusip && navigate(iborPositionPath(loan.cusip))}>
                        <Briefcase size={15} />
                      </IconButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
      {!loading && <Subtext>{visibleLoans.length} of {loans.length} governed loan reference records</Subtext>}
    </Workspace>
  );
};

export default CreditLoanReferenceWorkspace;
